import React, { useState } from 'react';
import styles from './MenuTree.module.scss';
import { LINK_TYPE } from '../../../Hooks/NavigationMenu';

/**
 * A placement tree, rendered as nested editable rows.
 *
 * Two states it makes visible on purpose, because both are real and neither
 * should look like a rendering bug:
 *   - a placement whose item is disabled everywhere (`item.isActive === false`)
 *     is greyed, not hidden;
 *   - a placement hidden in this menu only (`isVisible === false`) is labelled
 *     as such. The seed ships exactly one — the Degrees overview row.
 *
 * Reordering WITHIN a sibling group is by explicit up/down. Three reasons it
 * stays that way: it is keyboard-reachable and screen-reader-announceable for
 * free, it cannot be triggered by accident on a touch device, and it maps
 * exactly onto the per-sibling-group write the API expects.
 *
 * MOVING A ROW BETWEEN PARENTS (2026-09-29) is drag-and-drop, because up/down
 * cannot express it — there is no «out one level» the API models. It is
 * offered ONLY on the nesting menus («القائمة الرئيسية» and «قائمة البحث»);
 * a flat menu has no parent to move between. Two deliberate limits:
 *   - the drag starts from a GRIP, never the row body, so the row's buttons
 *     stay clickable and its text stays selectable;
 *   - a drop is only offered where `planPlacementMove` says it is legal, so
 *     the indicator never promises a write that will be refused.
 * It is pointer-only. The up/down buttons remain the keyboard path for order;
 * re-parenting by keyboard is NOT yet covered — see the gap note in
 * DPA-FE `docs/Controlled/MainMenu/11-what-was-built.md`.
 *
 * REDESIGNED 2026-09-29:
 *   - A row is now TWO LINES — title above, link below — instead of one line
 *     that wrapped wherever it ran out of room. The old layout produced a
 *     different shape per row depending on how long the title was, which read
 *     as broken rendering; this one is identical at every width.
 *   - Parents collapse. `collapsedIds` is owned by the screen so «طيّ الكل» can
 *     drive it, and so a filter can force everything open.
 */

/* --------------------------------------------------------------- glyphs -- */
/* Inline SVG, stroke = currentColor. The first build used emoji (👁 ✎ 🗑),
   which render through the OS emoji font: multicoloured, drawn differently on
   every machine, and blind to the hover/disabled colours beside them. These
   inherit their colour exactly like text.

   Exported because the settings screen's toolbar draws from the same set — two
   definitions of «add» would drift apart. */
const Glyph = ({ size = 15, children }) => (
  <svg
    viewBox='0 0 24 24'
    width={size}
    height={size}
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
    focusable='false'
  >
    {children}
  </svg>
);

export const ICONS = {
  up: (
    <Glyph size={10}>
      <polyline points='18 15 12 9 6 15' />
    </Glyph>
  ),
  down: (
    <Glyph size={10}>
      <polyline points='6 9 12 15 18 9' />
    </Glyph>
  ),
  show: (
    <Glyph>
      <path d='M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z' />
      <circle cx='12' cy='12' r='3' />
    </Glyph>
  ),
  hide: (
    <Glyph>
      <path d='M17.94 17.94A10 10 0 0 1 12 20c-7 0-11-8-11-8a18.4 18.4 0 0 1 5.06-5.94' />
      <path d='M9.9 4.24A9 9 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19' />
      <path d='M14.12 14.12a3 3 0 1 1-4.24-4.24' />
      <line x1='1' y1='1' x2='23' y2='23' />
    </Glyph>
  ),
  add: (
    <Glyph>
      <line x1='12' y1='5' x2='12' y2='19' />
      <line x1='5' y1='12' x2='19' y2='12' />
    </Glyph>
  ),
  edit: (
    <Glyph>
      <path d='M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z' />
    </Glyph>
  ),
  remove: (
    <Glyph>
      <polyline points='3 6 5 6 21 6' />
      <path d='M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2' />
    </Glyph>
  ),
  // The chevron points DOWN when open and toward the inline-start edge when
  // closed; the row flips it with a transform so one glyph serves both, and the
  // rotation direction follows `dir` rather than being baked in here.
  chevron: (
    <Glyph size={13}>
      <polyline points='6 9 12 15 18 9' />
    </Glyph>
  ),
  // Two columns of dots — the universal «drag me» grip. Filled, not stroked:
  // a 1px-radius ring reads as noise at this size.
  grip: (
    <Glyph size={14}>
      {[6, 12, 18].map((y) =>
        [9, 15].map((x) => (
          <circle
            key={`${x}-${y}`}
            cx={x}
            cy={y}
            r='1.4'
            fill='currentColor'
            stroke='none'
          />
        ))
      )}
    </Glyph>
  ),
  expandAll: (
    <Glyph size={14}>
      <polyline points='7 13 12 18 17 13' />
      <polyline points='17 11 12 6 7 11' />
    </Glyph>
  ),
  collapseAll: (
    <Glyph size={14}>
      <polyline points='7 18 12 13 17 18' />
      <polyline points='17 6 12 11 7 6' />
    </Glyph>
  ),
};

/** Reserved keys the website resolves rather than navigates to. See D3/D4. */
const describeLink = (placement) => {
  const item = placement?.item ?? {};
  const link =
    placement?.effectiveLink ?? placement?.linkOverride ?? item.dynamicUrl ?? '';

  if (item.linkType === LINK_TYPE.EXTERNAL) {
    return { text: item.externalUrl || link, kind: 'external', label: 'رابط خارجي' };
  }
  if (link.startsWith('action:')) {
    return { text: link, kind: 'action', label: 'إجراء' };
  }
  if (link.startsWith('#section-')) {
    return { text: link, kind: 'anchor', label: 'تمرير إلى قسم' };
  }
  // A catalogued page section (`/#news`). Checked AFTER `#section-n`, which
  // also contains a hash but is a fixed scroll index, not a catalogue row.
  if (link.includes('#')) {
    return { text: link, kind: 'anchor', label: 'قسم في صفحة' };
  }
  if (link.startsWith('/degree-programs/degree/')) {
    return { text: link, kind: 'path', label: 'برنامج أكاديمي' };
  }
  if (item.page?.slug) {
    return { text: item.page.slug, kind: 'page', label: 'صفحة داخلية' };
  }
  if (link) {
    return { text: link, kind: 'path', label: 'صفحة ديناميكية' };
  }
  return { text: '—', kind: 'none', label: 'بدون رابط' };
};

/**
 * Which third of the row the pointer is over: the edges re-order, the middle
 * re-parents. 30% rather than a straight third — «into» is the harder gesture
 * to aim at and the one an operator reaches for most, so it gets the widest
 * band.
 */
const dropZoneFor = (event) => {
  const rect = event.currentTarget.getBoundingClientRect();
  const offset = event.clientY - rect.top;
  const edge = rect.height * 0.3;
  if (offset < edge) return 'before';
  if (offset > rect.height - edge) return 'after';
  return 'inside';
};

const MenuRow = ({
  placement,
  depth,
  index,
  siblingCount,
  placementConfig,
  busyId,
  collapsedIds,
  onToggleCollapse,
  onEdit,
  onAddChild,
  onToggleVisible,
  onDelete,
  onMove,
  // Drag-to-reparent. Absent (a flat menu, or a filtered list) the grip is not
  // rendered at all, rather than rendered and inert.
  dragEnabled = false,
  draggingId = null,
  dropTarget = null,
  canDropOn,
  onRowDragStart,
  onRowDragOver,
  onRowDragLeave,
  onRowDrop,
  onRowDragEnd,
}) => {
  const item = placement?.item ?? {};
  const link = describeLink(placement);
  const isDisabled = item.isActive === false;
  const isHidden = placement.isVisible === false;
  const isBusy = busyId === placement.id;
  const canNest = placementConfig?.supportsNesting && depth === 0;
  const children = placement.children ?? [];
  const hasChildren = children.length > 0;
  const isCollapsed = hasChildren && collapsedIds.includes(placement.id);

  // `draggable` is armed by the GRIP's mousedown and disarmed on drag end, so
  // the row body is not draggable the rest of the time: a drag begun on the
  // title would fight text selection, and one begun on a button would fight
  // the click.
  const [armed, setArmed] = useState(false);
  const isDragging = draggingId === placement.id;
  const drop =
    dropTarget?.id === placement.id && draggingId && !isDragging
      ? dropTarget.position
      : null;

  const handleDragStart = (event) => {
    if (!dragEnabled || isBusy) return;
    event.dataTransfer.effectAllowed = 'move';
    // Firefox starts no drag at all without payload on the transfer.
    event.dataTransfer.setData('text/plain', placement.id);
    onRowDragStart?.(placement);
  };

  const handleDragOver = (event) => {
    if (!dragEnabled || !draggingId || isDragging) return;
    const position = dropZoneFor(event);
    // Refusing here is what keeps the cursor honest: no drop indicator and no
    // «move» cursor for a drop `planPlacementMove` would reject anyway.
    if (!canDropOn?.(placement, position)) {
      onRowDragLeave?.(placement);
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    onRowDragOver?.(placement, position);
  };

  const handleDrop = (event) => {
    if (!dragEnabled || !draggingId || isDragging) return;
    event.preventDefault();
    event.stopPropagation();
    onRowDrop?.(placement, dropZoneFor(event));
  };

  const handleDragEnd = () => {
    setArmed(false);
    onRowDragEnd?.();
  };

  return (
    <li className={styles['row-wrapper']}>
      <div
        draggable={dragEnabled && armed && !isBusy}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragLeave={() => onRowDragLeave?.(placement)}
        onDrop={handleDrop}
        className={[
          styles.row,
          isDisabled ? styles['row-disabled'] : '',
          isHidden ? styles['row-hidden'] : '',
          isBusy ? styles['row-busy'] : '',
          isDragging ? styles['row-dragging'] : '',
          drop ? styles[`drop-${drop}`] : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {dragEnabled && (
          <div className={styles['row-grip']}>
            <button
              type='button'
              className={styles['grip-button']}
              disabled={isBusy}
              onMouseDown={() => setArmed(true)}
              onMouseUp={() => setArmed(false)}
              aria-label={`اسحب «${item.title}» لنقله تحت عنصر رئيسي آخر`}
              title='اسحب لنقل العنصر تحت عنصر رئيسي آخر'
            >
              {ICONS.grip}
            </button>
          </div>
        )}

        <div className={styles['row-order']}>
          <button
            type='button'
            className={styles['order-button']}
            disabled={index === 0 || isBusy}
            onClick={() => onMove(placement, -1)}
            aria-label={`تحريك «${item.title}» للأعلى`}
            title='تحريك للأعلى'
          >
            {ICONS.up}
          </button>
          <button
            type='button'
            className={styles['order-button']}
            disabled={index === siblingCount - 1 || isBusy}
            onClick={() => onMove(placement, 1)}
            aria-label={`تحريك «${item.title}» للأسفل`}
            title='تحريك للأسفل'
          >
            {ICONS.down}
          </button>
        </div>

        {/* The slot is rendered even for a leaf, empty — without it every
            childless row's title would start 26px closer to the edge than its
            siblings', and the column would visibly zig-zag. */}
        <div className={styles['row-toggle']}>
          {hasChildren && (
            <button
              type='button'
              className={`${styles['toggle-button']} ${
                isCollapsed ? styles['toggle-closed'] : ''
              }`}
              onClick={() => onToggleCollapse(placement.id)}
              aria-expanded={!isCollapsed}
              aria-label={
                isCollapsed
                  ? `توسيع «${item.title}» (${children.length} عنصر فرعي)`
                  : `طيّ «${item.title}»`
              }
              title={isCollapsed ? 'توسيع' : 'طيّ'}
            >
              {ICONS.chevron}
            </button>
          )}
        </div>

        <div className={styles['row-main']}>
          <div className={styles['row-line']}>
            <span className={styles['row-title']}>{item.title || '—'}</span>
            {hasChildren && (
              <span className={styles['row-children']}>
                {children.length} فرعي
              </span>
            )}
            {isHidden && (
              <span className={styles['flag-hidden']}>مخفي في هذه القائمة</span>
            )}
            {isDisabled && (
              <span className={styles['flag-disabled']}>معطّل في كل القوائم</span>
            )}
            {item.isHeadTitle && (
              <span className={styles['flag-head']}>عنوان مجموعة</span>
            )}
            {item.openInNewTab && <span className={styles.flag}>نافذة جديدة</span>}
            {placementConfig?.supportsButtons && item.isDisplayTypeButton && (
              <span className={styles.flag}>زر</span>
            )}
          </div>

          <div className={`${styles['row-line']} ${styles['row-line-meta']}`}>
            {/* `.filter(Boolean)` matters: a kind with no colour rule of its
                own would otherwise render the literal string "undefined" as a
                class name. */}
            <span
              className={[styles['link-label'], styles[`link-${link.kind}`]]
                .filter(Boolean)
                .join(' ')}
            >
              {link.label}
            </span>
            <code className={styles['link-text']} title={link.text}>
              {link.text}
            </code>
          </div>
        </div>

        <div className={styles['row-actions']}>
          <button
            type='button'
            className={styles['action-button']}
            disabled={isBusy}
            onClick={() => onToggleVisible(placement)}
            aria-label={
              isHidden
                ? `إظهار «${item.title}» في هذه القائمة`
                : `إخفاء «${item.title}» من هذه القائمة`
            }
            title={isHidden ? 'إظهار' : 'إخفاء'}
          >
            {isHidden ? ICONS.hide : ICONS.show}
          </button>

          {canNest && (
            <button
              type='button'
              className={styles['action-button']}
              disabled={isBusy}
              onClick={() => onAddChild(placement)}
              aria-label={`إضافة عنصر فرعي تحت «${item.title}»`}
              title='إضافة عنصر فرعي'
            >
              {ICONS.add}
            </button>
          )}

          <button
            type='button'
            className={styles['action-button']}
            disabled={isBusy}
            onClick={() => onEdit(placement)}
            aria-label={`تعديل «${item.title}»`}
            title='تعديل'
          >
            {ICONS.edit}
          </button>

          <button
            type='button'
            className={`${styles['action-button']} ${styles['action-danger']}`}
            disabled={isBusy}
            onClick={() => onDelete(placement)}
            aria-label={`حذف «${item.title}» من هذه القائمة`}
            title='حذف'
          >
            {ICONS.remove}
          </button>
        </div>
      </div>

      {hasChildren && !isCollapsed && (
        <ul className={styles.children}>
          {children.map((child, childIndex) => (
            <MenuRow
              key={child.id}
              placement={child}
              depth={depth + 1}
              index={childIndex}
              siblingCount={children.length}
              placementConfig={placementConfig}
              busyId={busyId}
              collapsedIds={collapsedIds}
              onToggleCollapse={onToggleCollapse}
              onEdit={onEdit}
              onAddChild={onAddChild}
              onToggleVisible={onToggleVisible}
              onDelete={onDelete}
              onMove={onMove}
              dragEnabled={dragEnabled}
              draggingId={draggingId}
              dropTarget={dropTarget}
              canDropOn={canDropOn}
              onRowDragStart={onRowDragStart}
              onRowDragOver={onRowDragOver}
              onRowDragLeave={onRowDragLeave}
              onRowDrop={onRowDrop}
              onRowDragEnd={onRowDragEnd}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

const MenuTree = ({
  nodes,
  placement,
  collapsedIds = [],
  onToggleCollapse,
  emptyMessage = 'لا توجد عناصر في هذه القائمة بعد.',
  onAddFirst,
  ...handlers
}) => {
  if (!Array.isArray(nodes) || nodes.length === 0) {
    // An empty state that DOES something: the add button lives up in the panel
    // header, a full viewport away from where the operator's eye is when this
    // message appears. Absent (list at cap, or a filter returning nothing), the
    // message stands alone.
    return (
      <div className={styles.empty}>
        <p className={styles['empty-text']}>{emptyMessage}</p>
        {onAddFirst && (
          <button
            type='button'
            className={styles['empty-add']}
            onClick={onAddFirst}
          >
            {ICONS.add}
            إضافة أول عنصر
          </button>
        )}
      </div>
    );
  }

  return (
    <ul className={styles.tree}>
      {nodes.map((node, index) => (
        <MenuRow
          key={node.id}
          placement={node}
          depth={0}
          index={index}
          siblingCount={nodes.length}
          placementConfig={placement}
          collapsedIds={collapsedIds}
          onToggleCollapse={onToggleCollapse}
          {...handlers}
        />
      ))}
    </ul>
  );
};

export default MenuTree;
